import {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';

export type AuthUser = {
  username: string;
  contact: string;
  email?: string;
  profilePicture?: string;
}

interface AuthContextType {
  token: string | null;
  user: AuthUser | null;
  updateUser: (user: AuthUser) => void;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  isAuthenticated: boolean;
}

export const SESSION_KEY = "authSession";
const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60;


const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({
  children,
}: {
  children: ReactNode;
}) {

  const [token, setToken] = useState<string | null>(() => {
    // if (isSessionExpired(SESSION_KEY)) {
    //   localStorage.removeItem("token");
    //   return null;
    // }
    return localStorage.getItem("token");
  });

  const [user, setUser] = useState<AuthUser | null>(() => {
    // if (isSessionExpired(SESSION_KEY)) {
    //   localStorage.removeItem("user");
    //   return null;
    // }
    const userString = localStorage.getItem("user");
    return userString ? JSON.parse(userString) : null;
  });



  const updateUser = (user: AuthUser) => {
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
  };


  const login = (newToken: string, user: AuthUser) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(user));

    // expires in 30 minutes
    setSession(SESSION_KEY, "active", THIRTY_DAYS_IN_MINUTES);

    setToken(newToken);
    setUser(user);
  };



  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem(SESSION_KEY);

    setToken(null);
    setUser(null);
  };


  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        updateUser,
        login,
        logout,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const setSession = (
  key: string,
  value: string,
  ttlMinutes: number
) => {
  sessionStorage.setItem(
    key,
    JSON.stringify({
      value,
      expiry: Date.now() + ttlMinutes * 60 * 1000,
    })
  );
};


const getSession = (key: string): string | null => {
  if (isSessionExpired(key)) {
    return null;
  }
  const item = JSON.parse(sessionStorage.getItem(key)!);
  return item.value;
};

export const isSessionExpired = (key: string): boolean => {
  const itemStr = sessionStorage.getItem(key);

  if (!itemStr) {
    return true;
  }

  try {
    const item = JSON.parse(itemStr);

    if (Date.now() > item.expiry) {
      sessionStorage.removeItem(key);
      return true;
    }

    return false;
  } catch {
    sessionStorage.removeItem(key);
    return true;
  }
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}